import { z } from 'zod';
import { createOffsetPaginatedSchema, OffsetPaginationParamsSchema } from '../offset-pagination.js';
import { KitBatchStockDtoSchema } from './shipments.schema.js';
// ---------------------------------------------------------------------------
// Kit demand forecast, shortages and expiry warnings (domain 5,
// REQ-KIT-027, REQ-KIT-028, REQ-KIT-029).
//
// REQ-KIT-027 — the current stock is the starting point (income minus usage,
//   usage removals and corrections), kept separately for open-ended and
//   date-bearing kits.
// REQ-KIT-028 — demand is computed per kit type from the visits that need the
//   kit, using a virtual FEFO allocation (nearest visits consume the batches
//   with the shortest expiry first).
// REQ-KIT-029 — when stock cannot cover demand, the response reports how many
//   days remain until the shortage so the site can order in time.
// ---------------------------------------------------------------------------
/** Default planning horizon in weeks (REQ-KIT-028). */
export const KIT_DEMAND_FORECAST_DEFAULT_HORIZON_WEEKS = 8;
/** Batches within this many days of expiry raise a "expiring soon" warning. */
export const KIT_DEMAND_FORECAST_EXPIRING_SOON_DAYS = 30;
export const KitDemandForecastQuerySchema = OffsetPaginationParamsSchema.extend({
    trialId: z.coerce.number().int().positive(),
    trialLaboratoryId: z.coerce.number().int().positive().optional(),
    horizonWeeks: z.coerce
        .number()
        .int()
        .min(1)
        .max(52)
        .default(KIT_DEMAND_FORECAST_DEFAULT_HORIZON_WEEKS),
});
/**
 * A single visit whose kit need cannot be fully covered by the virtual FEFO
 * allocation, with the resulting uncovered quantity.
 */
export const KitDemandShortageVisitDtoSchema = z.object({
    patientVisitId: z.number().int().positive(),
    trialVisitId: z.number().int().positive(),
    visitName: z.string(),
    patientId: z.number().int().positive(),
    patientName: z.string(),
    /** Null for visits without a scheduled date (reserved pessimistically, REQ-KIT-028). */
    dateScheduled: z.iso.date().nullable(),
    /** Calendar days from today until the visit; null when the visit is unscheduled. */
    daysUntil: z.number().int().nullable(),
    /** Kits the visit needs but the virtual allocation could not cover. */
    kitsShort: z.number().int().positive(),
});
/**
 * Forecast state for one kit type in a trial, combining stock (REQ-KIT-027) with
 * demand (REQ-KIT-028) and the shortage horizon (REQ-KIT-029).
 */
export const KitDemandForecastItemDtoSchema = z.object({
    labKitId: z.number().int().positive(),
    kitName: z.string(),
    trialLaboratoryId: z.number().int().positive(),
    laboratoryName: z.string(),
    expiration: z.boolean(),
    localisation: z.string().nullable(),
    /** Current stock per REQ-KIT-027. */
    totalStock: z.number().int(),
    /** Total kits required by visits within the planning horizon. */
    requiredKits: z.number().int(),
    /** Kits the virtual FEFO allocation could actually cover. */
    allocatedKits: z.number().int(),
    /** requiredKits - allocatedKits (never negative). */
    shortageKits: z.number().int(),
    hasShortage: z.boolean(),
    /** Visit date on which the first uncovered need appears; null when covered. */
    firstShortageDate: z.iso.date().nullable(),
    /** REQ-KIT-029: days from today until the first shortage; null when covered. */
    daysUntilShortage: z.number().int().nullable(),
    /** Visits without a scheduled date that still reserve kits (shown separately). */
    unscheduledVisits: z.number().int(),
    /** Batches that are expired or expire within the "expiring soon" window. */
    expiringSoonBatches: z.array(KitBatchStockDtoSchema),
    /** Visits whose need exceeds the virtual allocation, nearest first. */
    shortageVisits: z.array(KitDemandShortageVisitDtoSchema),
});
export const KitDemandForecastResponseSchema = createOffsetPaginatedSchema(KitDemandForecastItemDtoSchema);
//# sourceMappingURL=demand-forecast.schema.js.map